// Learning System for UniTrade Chatbot
// This system captures unanswered questions and helps improve the chatbot

class ChatbotLearningSystem {
  constructor() {
    this.unansweredQuestions = this.loadUnansweredQuestions();
    this.questionFrequency = this.loadQuestionFrequency();
    this.learningEnabled = true;
  }

  // Load unanswered questions from localStorage
  loadUnansweredQuestions() {
    try {
      const stored = localStorage.getItem('unitrade_unanswered_questions');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading unanswered questions:', error);
      return [];
    }
  }

  // Load question frequency data
  loadQuestionFrequency() {
    try {
      const stored = localStorage.getItem('unitrade_question_frequency');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading question frequency:', error);
      return {};
    }
  }

  // Save unanswered questions to localStorage
  saveUnansweredQuestions() {
    try {
      localStorage.setItem('unitrade_unanswered_questions', JSON.stringify(this.unansweredQuestions));
    } catch (error) {
      console.error('Error saving unanswered questions:', error);
    }
  }

  // Save question frequency data
  saveQuestionFrequency() {
    try {
      localStorage.setItem('unitrade_question_frequency', JSON.stringify(this.questionFrequency));
    } catch (error) {
      console.error('Error saving question frequency:', error);
    }
  }

  // Record an unanswered question
  recordUnansweredQuestion(question, context = {}) {
    if (!this.learningEnabled || !question || question.trim() === '') {
      return;
    }

    const normalizedQuestion = question.toLowerCase().trim();
    
    // Check if this question already exists
    const existingIndex = this.unansweredQuestions.findIndex(
      item => item.question.toLowerCase() === normalizedQuestion
    );

    if (existingIndex !== -1) {
      // Increment frequency for existing question
      this.unansweredQuestions[existingIndex].frequency += 1;
      this.unansweredQuestions[existingIndex].lastAsked = new Date().toISOString();
    } else {
      // Add new unanswered question
      this.unansweredQuestions.push({
        question: question.trim(),
        frequency: 1,
        firstAsked: new Date().toISOString(),
        lastAsked: new Date().toISOString(),
        context: context,
        id: Date.now() + Math.random()
      });
    }

    // Keep only the most recent 100 unanswered questions
    if (this.unansweredQuestions.length > 100) {
      this.unansweredQuestions.sort((a, b) => b.frequency - a.frequency);
      this.unansweredQuestions = this.unansweredQuestions.slice(0, 100);
    }

    this.saveUnansweredQuestions();
  }

  // Record question frequency (for all questions, answered or not)
  recordQuestionFrequency(question) {
    if (!this.learningEnabled || !question || question.trim() === '') {
      return;
    }

    const normalizedQuestion = question.toLowerCase().trim();
    
    if (this.questionFrequency[normalizedQuestion]) {
      this.questionFrequency[normalizedQuestion].count += 1;
      this.questionFrequency[normalizedQuestion].lastAsked = new Date().toISOString();
    } else {
      this.questionFrequency[normalizedQuestion] = {
        count: 1,
        firstAsked: new Date().toISOString(),
        lastAsked: new Date().toISOString()
      };
    }

    this.saveQuestionFrequency();
  }

  // Get most frequently unanswered questions
  getMostFrequentUnansweredQuestions(limit = 10) {
    return this.unansweredQuestions
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, limit);
  }

  // Get recent unanswered questions
  getRecentUnansweredQuestions(limit = 10) {
    return this.unansweredQuestions
      .sort((a, b) => new Date(b.lastAsked) - new Date(a.lastAsked))
      .slice(0, limit);
  }

  // Get question frequency statistics
  getQuestionStats() {
    const totalQuestions = Object.keys(this.questionFrequency).length;
    const totalAsked = Object.values(this.questionFrequency).reduce((sum, q) => sum + q.count, 0);
    const unansweredCount = this.unansweredQuestions.length;
    
    return {
      totalUniqueQuestions: totalQuestions,
      totalQuestionsAsked: totalAsked,
      unansweredQuestions: unansweredCount,
      answerRate: totalQuestions > 0 ? ((totalQuestions - unansweredCount) / totalQuestions * 100).toFixed(1) : 0
    };
  }

  // Suggest similar questions based on keywords
  suggestSimilarQuestions(question, limit = 3) {
    if (!question || question.trim() === '') return [];

    const keywords = question.toLowerCase().split(' ').filter(word => word.length > 3);
    const suggestions = [];

    // Look through answered questions for similar ones
    Object.keys(this.questionFrequency).forEach(q => {
      const questionWords = q.split(' ');
      const matchCount = keywords.filter(keyword => 
        questionWords.some(word => word.includes(keyword) || keyword.includes(word))
      ).length;

      if (matchCount > 0) {
        suggestions.push({
          question: q,
          matchScore: matchCount,
          frequency: this.questionFrequency[q].count
        });
      }
    });

    return suggestions
      .sort((a, b) => b.matchScore - a.matchScore || b.frequency - a.frequency)
      .slice(0, limit)
      .map(s => s.question);
  }

  // Clear learning data (for admin use)
  clearLearningData() {
    this.unansweredQuestions = [];
    this.questionFrequency = {};
    localStorage.removeItem('unitrade_unanswered_questions');
    localStorage.removeItem('unitrade_question_frequency');
  }

  // Export learning data for analysis
  exportLearningData() {
    return {
      unansweredQuestions: this.unansweredQuestions,
      questionFrequency: this.questionFrequency,
      stats: this.getQuestionStats(),
      exportDate: new Date().toISOString()
    };
  }

  // Disable/enable learning
  setLearningEnabled(enabled) {
    this.learningEnabled = enabled;
  }
}

// Create singleton instance
const learningSystem = new ChatbotLearningSystem();

export default learningSystem;

// Helper functions for easy integration
export const recordUnansweredQuestion = (question, context) => {
  learningSystem.recordUnansweredQuestion(question, context);
};

export const recordQuestionFrequency = (question) => {
  learningSystem.recordQuestionFrequency(question);
};

export const getLearningStats = () => {
  return learningSystem.getQuestionStats();
};

export const getMostAskedUnansweredQuestions = (limit = 10) => {
  return learningSystem.getMostFrequentUnansweredQuestions(limit);
};

export const getSuggestedQuestions = (question, limit = 3) => {
  return learningSystem.suggestSimilarQuestions(question, limit);
};
